/**
 * `/support/messages/:id` — one support conversation.
 *
 * Messages the customer sent sit on the right in blue ("You · Sent");
 * replies from Provide Cars sit on the left in grey ("Provide Cars Support ·
 * Received"), oldest first. The reply box at the bottom writes into the same
 * thread; writing into a closed thread reopens it.
 *
 * Only the caller's own thread is returned by the BFF (404 otherwise).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card, CardBody, ErrorState, Spinner } from '../components/ui';
import { SupportStatusPill } from '../components/support/SupportStatusPill';
import { PortalApiError } from '../lib/api';
import {
  SUPPORT_BODY_MAX,
  formatSupportTime,
  getSupportThread,
  newRequestId,
  sendSupportReply,
  supportErrorCode,
  type SupportEntry,
  type SupportThreadDetail,
} from '../lib/supportApi';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'error'; code: string }
  | { kind: 'success'; thread: SupportThreadDetail };

export function SupportThreadPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!Number.isInteger(numericId) || numericId <= 0) {
      setLoad({ kind: 'not_found' });
      return;
    }
    const ctrl = new AbortController();
    setLoad({ kind: 'loading' });
    getSupportThread(numericId, ctrl.signal)
      .then((thread) => setLoad({ kind: 'success', thread }))
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalApiError && err.status === 404) {
          setLoad({ kind: 'not_found' });
          return;
        }
        setLoad({ kind: 'error', code: supportErrorCode(err) });
      });
    return () => ctrl.abort();
  }, [numericId]);

  return (
    <section data-testid="support-thread-page" style={{ display: 'grid', gap: '1.25rem' }}>
      <p style={{ margin: 0, fontSize: '0.875rem' }}>
        <Link to="/support" style={{ color: '#1f4ed8', fontWeight: 600, textDecoration: 'none' }}>
          ← Back to support
        </Link>
      </p>

      {load.kind === 'loading' ? (
        <div aria-busy="true" data-testid="support-thread-loading">
          <Spinner block label="Loading" />
        </div>
      ) : null}

      {load.kind === 'not_found' ? (
        <div data-testid="support-thread-not-found">
          <ErrorState
            title="Conversation not found"
            description="It may have been removed, or it belongs to another account."
          />
        </div>
      ) : null}

      {load.kind === 'error' ? (
        <div data-testid="support-thread-error">
          <ErrorState
            title="Failed to load"
            description={`Error code: ${load.code}`}
            onRetry={() => window.location.reload()}
          />
        </div>
      ) : null}

      {load.kind === 'success' ? (
        <Conversation
          thread={load.thread}
          onUpdated={(thread) => setLoad({ kind: 'success', thread })}
        />
      ) : null}
    </section>
  );
}

function Conversation({
  thread,
  onUpdated,
}: {
  thread: SupportThreadDetail;
  onUpdated: (thread: SupportThreadDetail) => void;
}): JSX.Element {
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const requestIdRef = useRef<string | null>(null);

  const trimmed = reply.trim();
  const canSend = !sending && trimmed.length > 0 && trimmed.length <= SUPPORT_BODY_MAX;

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSend) return;
    if (!requestIdRef.current) requestIdRef.current = newRequestId();
    setSending(true);
    setSendError(null);
    try {
      const updated = await sendSupportReply(thread.id, {
        body: trimmed,
        requestId: requestIdRef.current,
      });
      setReply('');
      requestIdRef.current = null;
      onUpdated(updated);
    } catch (err: unknown) {
      setSendError(supportErrorCode(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <header style={{ display: 'grid', gap: '0.375rem' }}>
        <h1
          style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#111827',
            overflowWrap: 'anywhere',
          }}
        >
          {thread.subject}
        </h1>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
          <SupportStatusPill thread={thread} />
          <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
            Started {formatSupportTime(thread.created_at)}
          </span>
        </div>
      </header>

      <Card aria-label="Conversation">
        <CardBody>
          <ol
            data-testid="support-conversation"
            style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '0.875rem' }}
          >
            {thread.entries.map((e) => (
              <Message key={e.id} entry={e} />
            ))}
          </ol>
        </CardBody>
      </Card>

      <Card aria-label="Reply">
        <CardBody>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '0.75rem' }}>
            <label
              style={{
                display: 'flex',
                flexDirection: 'column',
                fontSize: '0.75rem',
                color: '#374151',
              }}
            >
              <span style={{ marginBottom: '0.25rem', fontWeight: 500 }}>
                Your reply ({trimmed.length}/{SUPPORT_BODY_MAX})
              </span>
              <textarea
                data-testid="support-reply-input"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                maxLength={SUPPORT_BODY_MAX}
                rows={4}
                placeholder="Write a reply to Provide Cars"
                style={{
                  padding: '0.5rem 0.625rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.25rem',
                  fontSize: '0.875rem',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </label>
            {thread.status === 'closed' ? (
              <p style={{ margin: 0, fontSize: '0.8125rem', color: '#4b5563' }}>
                This conversation is closed. Sending a reply reopens it.
              </p>
            ) : null}
            {sendError ? (
              <p
                data-testid="support-reply-error"
                role="alert"
                style={{ margin: 0, color: '#991b1b', fontSize: '0.8125rem' }}
              >
                Your reply could not be sent ({sendError}). Please try again in a moment.
              </p>
            ) : null}
            <div>
              <Button
                type="submit"
                variant="primary"
                disabled={!canSend}
                data-testid="support-reply-button"
              >
                {sending ? 'Sending…' : 'Send reply'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </>
  );
}

function Message({ entry }: { entry: SupportEntry }): JSX.Element {
  const sent = entry.author === 'customer';
  return (
    <li
      data-testid="support-message"
      data-direction={sent ? 'sent' : 'received'}
      style={{ display: 'flex', justifyContent: sent ? 'flex-end' : 'flex-start' }}
    >
      <div
        style={{
          maxWidth: 'min(100%, 36rem)',
          padding: '0.625rem 0.875rem',
          borderRadius: sent
            ? '0.75rem 0.75rem 0.25rem 0.75rem'
            : '0.75rem 0.75rem 0.75rem 0.25rem',
          background: sent ? '#eff6ff' : '#f3f4f6',
          border: `1px solid ${sent ? '#bfdbfe' : '#e5e7eb'}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.25rem 0.5rem',
            marginBottom: '0.25rem',
            fontSize: '0.75rem',
            color: '#4b5563',
          }}
        >
          <span
            style={{
              padding: '0.0625rem 0.375rem',
              borderRadius: '0.25rem',
              fontWeight: 700,
              background: sent ? '#1f4ed8' : '#374151',
              color: '#fff',
            }}
          >
            {sent ? 'Sent' : 'Received'}
          </span>
          <strong style={{ color: '#111827' }}>{sent ? 'You' : 'Provide Cars Support'}</strong>
          <span>{formatSupportTime(entry.created_at)}</span>
        </div>
        <p
          style={{
            margin: 0,
            fontSize: '0.875rem',
            color: '#111827',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {entry.body}
        </p>
      </div>
    </li>
  );
}
